import { useEffect, useState } from "react";
import { Modal, Spin, Typography } from "antd";
import { Link } from "react-router-dom";
import MainContainer from "../../containers/MainContainer";
import ContentTable from "../../components/ContentTable";
import toastNotification from "../../components/toastNotification";
import ActionMenu from "../../components/ActionMenu";
import { ReactComponent as MobileIcon } from "../../assets/icons/iconMobile.svg";
import { ReactComponent as PhoneIcon } from "../../assets/icons/iconPhone.svg";
import hospitalService from "../../services/hospitalService";
import userServices from "../../services/userServices";
import { useBreadCrumbContext } from "../../context/BreadCrumbContext";
import { RolesDisplay } from "../../config/role-config.json";

const { confirm } = Modal;
const { Text } = Typography;
const customPageSize = 10;

const hospitalUserRoles = ["HOSPITAL_ADMIN", "HOSPITAL_STAFF", "SURGEON"];

const HospitalUsersPage = (props) => {
  const {
    match: { url, params },
    history,
    location
  } = props;
  const paths = url.split("/");
  const isDeletedPage = paths.includes("deleted");
  const hospitalId = params.id ? params.id : null;

  const pageHeaderProps = isDeletedPage
    ? {
      title: "Hospital Deleted Users",
      backBtnPath: `/hospital/${hospitalId}/user`,
    }
    : {
      title: "Hospital Users",
      btnText: "Add User",
      onBtnClick: () => {
        history.push(`${url}/new`);
      },
      topActionMenuItems: [
        {
          title: "Show Deleted Users",
          path: `${url}/deleted`,
        },
      ],
    };

  const [hospitalUsersList, setHospitalUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeOut, setTimeOut] = useState(0);
  const [currentPage, setCurrentPage] = useState(location.state?.pageNumber ?? 1);
  const [userCount, setUserCount] = useState(0);
  const [userSearchText, setUserSearchText] = useState();
  const { setBreadCrumb } = useBreadCrumbContext();

  const actions = (record) => {
    return isDeletedPage
      ? [
        {
          title: "Restore",
          onClick: (menuItemProps) => {
            menuItemProps.domEvent.stopPropagation();
            onUserRestore(record);
          },
        },
      ]
      : [
        {
          title: "Edit",
          onClick: (menuItemProps) => {
            menuItemProps.domEvent.stopPropagation();
            setBreadCrumb((prev) => ({
              ...prev,
              user: `${record.firstName ?? ""} ${record.lastName ?? ""
                }`.trim(),
            }));
            history.push(`${url}/${record.id}`, {
              record,
              editMode: true,
              pageNumber: currentPage
            });
          },
        },
        !record.isActive
          ? {
            title: "Resend Invite",
            onClick: (menuItemProps) => {
              menuItemProps.domEvent.stopPropagation();
              sendActivationLink(record.email);
            },
          }
          : null,
        {
          title: "Delete",
          onClick: (menuItemProps) => {
            menuItemProps.domEvent.stopPropagation();
            showDeleteConfirmation(record.id);
          },
        },
      ];
  };

  const columnsList = [
    {
      title: "User's Name & Role",
      dataIndex: "firstName",
      key: "firstName",
      render: (name, record) => {
        const userName = name ? (
          <Text strong>
            {name} {record.middleName} {record.lastName}
          </Text>
        ) : (
            <Text style={{ color: "grey" }}>Not Set</Text>
          );
        return (
          <>
            {isDeletedPage ? (
              <>{userName}</>
            ) : (
                <Link
                  to={{
                    pathname: `/hospital/${hospitalId}/user/${record.id}`,
                    state: { record, pageNumber: currentPage },
                  }}
                  onClick={() =>
                    setBreadCrumb((prev) => ({
                      ...prev,
                      user: `${record.firstName ?? ""} ${record.lastName ?? ""
                        }`.trim(),
                    }))
                  }
                >
                  {userName}
                </Link>
              )}
            <br />
            <Text style={{ fontSize: "0.9em" }}>
              {RolesDisplay[record.role]}
            </Text>
            {!record.isActive && (
              <>
                <br />
                <Text style={{ color: "#db9f2e", fontSize: "0.9em" }}>
                  Activation Pending
                </Text>
              </>
            )}
          </>
        );
      },
    },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Contact",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (mobileNumber, { officeNumber }) => (
        <>
          {mobileNumber && (
            <span className="display-flex-center">
              <MobileIcon width="1.3em" />
              {mobileNumber}
            </span>
          )}
          {officeNumber && (
            <span className="display-flex-center">
              <PhoneIcon fill="#1d1d1d" width="1.3em" />
              {officeNumber}
            </span>
          )}
          {!mobileNumber && !officeNumber && (
            <Text style={{ color: "grey", marginLeft: "0.4em" }}>Not Set</Text>
          )}
        </>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      fixed: "right",
      width: "4rem",
      render: (value, record) => <ActionMenu actions={actions(record)} />,
    },
  ];

  const onUserRestore = async (user) => {
    setLoading(true);
    const res = await userServices.patchRestoreUser(user.id);  
    if (res) {
      toastNotification("success", "User Restored");
      populateHospitalUsersList(customPageSize, 0);
    } else {
      toastNotification("error", "Error restoring the user");
    }
  };

  const sendActivationLink = async (email) => {
    setLoading(true);
    const res = await userServices.postResendUserActivationLink(email);
    if (res.status === 200) {
      toastNotification(
        "success",
        "Activation link sent to the User’s email address"
      );
    } else {
      toastNotification("error", "Could not send the activation link");
    }
    setLoading(false);
  };

  const showDeleteConfirmation = (userId) => {
    confirm({
      title: "Delete Hospital User",
      content: "Are you sure you want to delete this User?",
      okText: "Delete",
      centered: true,
      cancelText: "Cancel",
      onOk() {
        deleteHospitalUser(userId);
      },
    });
  };

  const onSearchFunction = (searchValue) => {
    if (timeOut) {
      clearTimeout(timeOut);
    }
    setTimeOut(
      setTimeout(async () => {
        searchValue = searchValue.trim();
        setUserSearchText(searchValue);
        const success = await userServices.getUsersCount(
          "hospital",
          hospitalId,
          isDeletedPage,
          searchValue
        );
        setUserCount(success);
        setCurrentPage(1);
        populateHospitalUsersList(customPageSize, 0, searchValue);
      }, 500)
    );
  };

  /** API Calls */
  const populateHospitalUsersList = async (limit, skip, searchValue) => {
    setLoading(true);
    const data = await hospitalService.getHospitalUsers(
      hospitalId,
      hospitalUserRoles,
      isDeletedPage,
      limit,
      skip,
      searchValue
    );
    if (data) {
      setHospitalUsersList(data);
    } else {
      toastNotification("error", "Error fetching User records");
    }
    setLoading(false);
  };

  const deleteHospitalUser = async (id) => {
    setLoading(true);
    const isDeleted = await userServices.deleteUser(id);
    if (isDeleted) {
      toastNotification("success", "Hospital User deleted successfully");
      await populateHospitalUsersList(customPageSize, 0);
    } else {
      toastNotification("error", "Error deleting the user record");
    }
    setLoading(false);
  };

  /** API Calls End */

  useEffect(() => {
    setLoading(true);
    (async () => {
      const success = await userServices.getUsersCount(
        "hospital",
        hospitalId,
        isDeletedPage,
        userSearchText
      );

      if (success !== false) {
        setUserCount(success);
        if (success > 0) populateHospitalUsersList(customPageSize, customPageSize * (currentPage - 1));
        else setHospitalUsersList([]);
      } else {
        toastNotification("error", "Error fetching user records");
      }
      setLoading(false);
    })();
  }, []);

  return (
    <MainContainer
      searchPlaceholderTxt="Search by First Name, Last Name or Email"
      onSearchFunction={onSearchFunction}
      {...pageHeaderProps}
    >
      <Spin spinning={loading}>
        <ContentTable
          data={hospitalUsersList}
          columnsList={columnsList}
          pagination={{
            pageSize: customPageSize,
            total: userCount,
            current: currentPage,
            onChange: async (page, pageSize) => {
              setLoading(true);
              setCurrentPage(page);
              await populateHospitalUsersList(
                pageSize,
                pageSize * (page - 1),
                userSearchText
              );
              setLoading(false);
            },
          }}
        />
      </Spin>
    </MainContainer>
  );
};

export default HospitalUsersPage;
